/*
  Forced logout telemetry.

  Three interceptors sign the user out when authentication stops being valid: the
  Apollo error link (src/vue-apollo.js), the router guard that checks
  `expireConnection` (src/router/index.js) and the polling watcher (src/App.vue).

  Each of them used to build its own message as `Forced logout: <query> on <view>`.
  Sentry groups by message text, so putting the query and the view inside the text
  fragmented one single problem into ~49 issues over ~3.565 events — 14,6% of this
  project's volume (DL-1531).

  The message and the fingerprint are now fixed, so every forced logout lands on one
  issue. The context that used to fragment it travels as tags instead, which keeps it
  filterable in Sentry without splitting the group.
*/

import * as Sentry from "@sentry/vue";

const MESSAGE = "Forced logout";

// Explicit and constant: `captureMessage` would otherwise group by message *and*
// stack trace, and the three call sites have three different stacks.
const FINGERPRINT = ["forced-logout"];

const UNKNOWN = "unknown";

/**
 * Reports a forced logout to Sentry as a single grouped event.
 *
 * Never throws: telemetry must not be able to break the logout it is reporting.
 *
 * @param {Object} params
 * @param {string} params.trigger - which interceptor fired, e.g. "polling_isExpired"
 * @param {string} [params.page] - route name the user was on
 * @param {string} [params.userId] - id of the user being signed out
 * @param {Object} [params.tags] - extra Sentry tags, as { name: value }
 * @param {Object} [params.extra] - extra Sentry context, as { name: value }
 */
export const reportForcedLogout = ({
  trigger,
  page,
  userId,
  tags = {},
  extra = {},
} = {}) => {
  try {
    Sentry.withScope((scope) => {
      scope.setFingerprint(FINGERPRINT);
      scope.setLevel("warning");
      scope.setTag("session.forced_logout", "true");
      scope.setTag("session.trigger", trigger || UNKNOWN);
      scope.setTag("session.page", page || UNKNOWN);
      scope.setTag("session.user_id", userId || UNKNOWN);

      Object.entries(tags).forEach(([name, value]) => scope.setTag(name, value));
      Object.entries(extra).forEach(([name, value]) => scope.setExtra(name, value));

      Sentry.captureMessage(MESSAGE);
    });
  } catch (error) {
    console.warn("Sentry telemetry failed", error);
  }
};

export { MESSAGE as FORCED_LOGOUT_MESSAGE };
